/**
 * Decision guardrail domain events — catalog v9 additions (commerce-platform
 * Phase 5, plan §4 P5-A4 + spec §4.2).
 *
 * - `decision:verdict-issued`      — auditable verdict record. Carries the
 *   FULL probability distributions (spec §8.2 — every decision logged with
 *   full distributions), the question-set, τ, provider and breaker state, so
 *   the audit row + durable event are a complete, replayable decision record.
 *   Per-agent threshold fitting + drift monitoring (D7) reads these records.
 * - `decision:provider-degraded`   — emitted on breaker CLOSED→OPEN
 *   transitions (NOT per request, spec §4.2).
 *
 * PII note: the payload NEVER contains raw tool args — only `argsHash` inside
 * the structured question-set echo (spec §2, §8.4).
 */
import {
  asEventRecord,
  defineEvent,
  optionalString,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

const VERDICT_VALUES = [
  'allow',
  'challenge',
  'block',
  'escalate_to_human',
  'require_approval',
  'limit_scope',
] as const;

const PROVIDER_VALUES = ['qwen-decision-model-preview', 'null', 'none'] as const;

const BREAKER_VALUES = ['closed', 'open', 'half-open'] as const;

export interface DecisionVerdictIssuedPayload extends Record<string, unknown> {
  decisionId: string;
  orgId: string;
  agentId?: string;
  kind: string;
  action: string;
  confidence: number;
  tau: number;
  provider: string;
  cacheHit: boolean;
  breakerState: string;
  questionSet: Record<string, unknown>;
  distributions: Record<string, unknown>;
  runId?: string;
  conversationId?: string;
}

export interface DecisionProviderDegradedPayload extends Record<string, unknown> {
  provider: string;
  reason: string;
  failureCount: number;
  windowSeconds: number;
  orgId?: string;
}

function requireFiniteNumber(
  value: unknown,
  field: string,
  context: string,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${context}: field "${field}" must be a finite number`);
  }
  return value;
}

function requireBoolean(value: unknown, field: string, context: string): boolean {
  if (typeof value !== 'boolean') {
    throw new Error(`${context}: field "${field}" must be a boolean`);
  }
  return value;
}

function requireEnum(
  value: unknown,
  field: string,
  allowed: readonly string[],
  context: string,
): string {
  if (typeof value !== 'string' || !allowed.includes(value)) {
    throw new Error(`${context}: field "${field}" must be one of ${allowed.join(', ')}`);
  }
  return value;
}

function requireRecord(value: unknown, field: string, context: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${context}: field "${field}" must be a plain object`);
  }
  return value as Record<string, unknown>;
}

/**
 * The FULL distributions contract (spec §4.2 / §8.2): choice is a non-empty
 * numeric map over verdict labels; noul is a numeric array of P(yes) in
 * [0,1]; score is a numeric array of exactly 5 ordinal levels. A record that
 * cannot prove full distributions is rejected at emit.
 */
function requireDistributions(
  value: unknown,
  context: string,
): Record<string, unknown> {
  const distributions = requireRecord(value, 'distributions', context);

  const choice = requireRecord(distributions.choice, 'distributions.choice', context);
  const choiceKeys = Object.keys(choice);
  if (choiceKeys.length === 0) {
    throw new Error(`${context}: field "distributions.choice" must contain the full option distribution`);
  }
  for (const key of choiceKeys) {
    requireFiniteNumber(choice[key], `distributions.choice.${key}`, context);
  }

  if (!Array.isArray(distributions.noul)) {
    throw new Error(`${context}: field "distributions.noul" must be an array of P(yes) probabilities`);
  }
  for (const p of distributions.noul) {
    const n = requireFiniteNumber(p, 'distributions.noul[]', context);
    if (n < 0 || n > 1) {
      throw new Error(`${context}: field "distributions.noul[]" must be a probability in [0,1]`);
    }
  }

  if (!Array.isArray(distributions.score) || distributions.score.length !== 5) {
    throw new Error(`${context}: field "distributions.score" must be a 5-level ordinal distribution`);
  }
  for (const p of distributions.score) {
    requireFiniteNumber(p, 'distributions.score[]', context);
  }

  return {
    choice,
    noul: [...distributions.noul],
    score: [...(distributions.score as number[])],
  };
}

const VERDICT_ISSUED_TYPE = 'decision:verdict-issued';

const verdictIssuedSchema: EventSchema<DecisionVerdictIssuedPayload> = {
  parse(data) {
    const record = asEventRecord(data, VERDICT_ISSUED_TYPE);
    const agentId = optionalString(record.agentId, 'agentId', VERDICT_ISSUED_TYPE);
    const runId = optionalString(record.runId, 'runId', VERDICT_ISSUED_TYPE);
    const conversationId = optionalString(
      record.conversationId,
      'conversationId',
      VERDICT_ISSUED_TYPE,
    );
    return {
      decisionId: requireString(record.decisionId, 'decisionId', VERDICT_ISSUED_TYPE),
      orgId: requireString(record.orgId, 'orgId', VERDICT_ISSUED_TYPE),
      ...(agentId === undefined ? {} : { agentId }),
      ...(runId === undefined ? {} : { runId }),
      ...(conversationId === undefined ? {} : { conversationId }),
      kind: requireEnum(record.kind, 'kind', ['tool_call', 'session', 'frequency'], VERDICT_ISSUED_TYPE),
      action: requireEnum(record.action, 'action', VERDICT_VALUES, VERDICT_ISSUED_TYPE),
      confidence: requireFiniteNumber(record.confidence, 'confidence', VERDICT_ISSUED_TYPE),
      tau: requireFiniteNumber(record.tau, 'tau', VERDICT_ISSUED_TYPE),
      provider: requireEnum(record.provider, 'provider', PROVIDER_VALUES, VERDICT_ISSUED_TYPE),
      cacheHit: requireBoolean(record.cacheHit, 'cacheHit', VERDICT_ISSUED_TYPE),
      breakerState: requireEnum(record.breakerState, 'breakerState', BREAKER_VALUES, VERDICT_ISSUED_TYPE),
      questionSet: requireRecord(record.questionSet, 'questionSet', VERDICT_ISSUED_TYPE),
      distributions: requireDistributions(record.distributions, VERDICT_ISSUED_TYPE),
    };
  },
};

export const DECISION_VERDICT_ISSUED_EVENT = defineEvent<DecisionVerdictIssuedPayload>({
  type: VERDICT_ISSUED_TYPE,
  version: 1,
  schema: verdictIssuedSchema,
  auditable: true,
  description: 'A guardrail verdict was issued, with the full probability distributions, tau, provider and breaker state (P5-A4).',
  // additive P6-T04: voice-bridge call correlation fields
  since: '1.14.0',
});

const PROVIDER_DEGRADED_TYPE = 'decision:provider-degraded';

const providerDegradedSchema: EventSchema<DecisionProviderDegradedPayload> = {
  parse(data) {
    const record = asEventRecord(data, PROVIDER_DEGRADED_TYPE);
    const orgId = optionalString(record.orgId, 'orgId', PROVIDER_DEGRADED_TYPE);
    return {
      provider: requireEnum(record.provider, 'provider', PROVIDER_VALUES, PROVIDER_DEGRADED_TYPE),
      reason: requireString(record.reason, 'reason', PROVIDER_DEGRADED_TYPE),
      failureCount: requireFiniteNumber(record.failureCount, 'failureCount', PROVIDER_DEGRADED_TYPE),
      windowSeconds: requireFiniteNumber(record.windowSeconds, 'windowSeconds', PROVIDER_DEGRADED_TYPE),
      ...(orgId === undefined ? {} : { orgId }),
    };
  },
};

export const DECISION_PROVIDER_DEGRADED_EVENT = defineEvent<DecisionProviderDegradedPayload>({
  type: PROVIDER_DEGRADED_TYPE,
  version: 1,
  schema: providerDegradedSchema,
  auditable: true,
  description: 'The guardrail circuit breaker transitioned CLOSED to OPEN; verdicts fail closed to challenge.',
  since: '1.11.0',
});

export const DECISION_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [DECISION_VERDICT_ISSUED_EVENT.type]: DECISION_VERDICT_ISSUED_EVENT,
  [DECISION_PROVIDER_DEGRADED_EVENT.type]: DECISION_PROVIDER_DEGRADED_EVENT,
};
